// ItemEditor.tsx
import React from 'react';
import { Item } from './types';

interface ItemEditorProps {
  item: Item;
  sectionIndex: number;
  itemIndex: number;
  updateItemField: (sectionIndex: number, itemIndex: number, field: string, value: string) => void;
  isClientView?: boolean; // Flag to differentiate between EditTemplate and ClientForm
}

const ItemEditor: React.FC<ItemEditorProps> = ({ item, sectionIndex, itemIndex, updateItemField, isClientView = false }) => {
  const commonProps = (field: string, placeholder: string) => ({
    type: 'text',
    className: 'form-control mb-2',
    value: item[field as keyof typeof item] as string || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateItemField(sectionIndex, itemIndex, field, e.target.value),
    placeholder,
  });

  return (
    <div className="flex-grow-1 mb-2 mb-md-0 me-md-3">
      {item.type === 'song' && (
        <>
          {isClientView ? (
            <label className="form-label d-block"><strong>{item.label}:</strong></label>
          ) : (
            <input {...commonProps('label', 'Item Label (e.g., Walk Down the Aisle)')} />
          )}
          <input {...commonProps('song', 'Enter song')} />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'introduction' && (
        <>
          <input {...commonProps('name', 'Enter name')} />
          <input {...commonProps('song', 'Enter song')} />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'timeline' && (
        <>
          <input {...commonProps('event', 'Enter event')} />
          <input {...commonProps('time', 'Enter time (e.g., 7:00 PM)')} />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'general' && (
        <>
          {isClientView ? (
            <label className="form-label d-block"><strong>{item.key}:</strong></label>
          ) : (
            <input {...commonProps('key', 'Field Name (e.g., Client Name)')} />
          )}
          <input {...commonProps('value', isClientView ? `Enter ${item.key.toLowerCase()}` : 'Value (e.g., John & Jane Doe)')} />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'address' && (
        <>
          {isClientView ? (
            <label className="form-label d-block"><strong>{item.label}:</strong></label>
          ) : (
            <input {...commonProps('label', 'Address Label (e.g., Venue Location)')} />
          )}
          <input {...commonProps('street', 'Street')} />
          <div className="row">
            <div className="col-md-6">
              <input {...commonProps('city', 'City')} />
            </div>
            <div className="col-md-3">
              <input {...commonProps('state', 'State')} />
            </div>
            <div className="col-md-3">
              <input {...commonProps('zip', 'ZIP')} />
            </div>
          </div>
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'date' && (
        <>
          {isClientView ? (
            <label className="form-label d-block"><strong>{item.label}:</strong></label>
          ) : (
            <input {...commonProps('label', 'Date Label (e.g., Event Date)')} />
          )}
          <input
            type="date"
            className="form-control mb-2"
            value={item.date}
            onChange={(e) => updateItemField(sectionIndex, itemIndex, 'date', e.target.value)}
          />
          <input
            type="time"
            className="form-control mb-2"
            value={item.time || ''}
            onChange={(e) => updateItemField(sectionIndex, itemIndex, 'time', e.target.value)}
          />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
      {item.type === 'note' && (
        <>
          <textarea
            className="form-control mb-2"
            value={item.content}
            onChange={(e) => updateItemField(sectionIndex, itemIndex, 'content', e.target.value)}
            placeholder="Enter note content"
            style={{ height: `${item.height}px` }}
          />
          <input
            type="range"
            className="form-range mb-2"
            min="50"
            max="500"
            value={item.height}
            onChange={(e) => updateItemField(sectionIndex, itemIndex, 'height', e.target.value)}
          />
          <input {...commonProps(isClientView ? 'clientNotes' : 'notes', isClientView ? 'Additional details' : 'DJ Notes')} />
          {isClientView && item.notes && <small className="text-muted d-block mt-1">(DJ Notes: {item.notes})</small>}
        </>
      )}
    </div>
  );
};

export default ItemEditor;